/**
 * ─── Arena Matches Page ─────────────────────────────────────
 * Schedule fights, set odds, declare results, settle bets.
 */

import { useEffect, useState, useCallback } from 'react'
import { Card, CardHeader, Badge, Button, Input, Modal } from '@repo/ui-core'
import { arenaMatchRepo, arenaFighterRepo, arenaBetRepo } from '../../lib/db'
import { MATCH_STATUS_LABELS, MATCH_RESULT_LABELS, WEIGHT_CLASS_LABELS, ArenaBettingService } from '@repo/entity-arena'
import { Trophy, Plus, Swords, CheckCircle, XCircle } from 'lucide-react'

export function ArenaMatchPage() {
  const [matches, setMatches] = useState<any[]>([])
  const [fighters, setFighters] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ fighter1Id: '', fighter2Id: '', matchCode: '', scheduledDate: Date.now(),
    weightClass: 'open', fighter1Odds: 2.0, fighter2Odds: 2.5, drawOdds: 5.0, houseCommission: 0.05, referee: '', notes: '' })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [m, f] = await Promise.all([
        arenaMatchRepo.findMany({ page: 1, pageSize: 100, sort: [{ field: 'scheduledDate', direction: 'desc' }] }),
        arenaFighterRepo.findMany({ page: 1, pageSize: 500 }),
      ])
      setMatches('items' in m ? m.items : [])
      setFighters('items' in f ? f.items : [])
    } catch (e) { console.error(e) } finally { setLoading(false) }
  }, [])
  useEffect(() => { load() }, [load])

  async function save() {
    setSaving(true)
    try {
      const f1 = fighters.find((f: any) => f.id === form.fighter1Id)
      const f2 = fighters.find((f: any) => f.id === form.fighter2Id)
      const payload: any = { ...form, scheduledDate: Number(form.scheduledDate),
        fighter1Odds: Number(form.fighter1Odds), fighter2Odds: Number(form.fighter2Odds),
        drawOdds: Number(form.drawOdds), houseCommission: Number(form.houseCommission),
        fighter1Name: f1?.name, fighter2Name: f2?.name,
        arenaId: 'default', status: 'scheduled', totalBetAmount: 0,
        tenantId: 'default', createdBy: 'admin', updatedBy: 'admin' }
      await arenaMatchRepo.create(payload)
      setShowModal(false); setForm({ fighter1Id: '', fighter2Id: '', matchCode: '', scheduledDate: Date.now(),
        weightClass: 'open', fighter1Odds: 2.0, fighter2Odds: 2.5, drawOdds: 5.0, houseCommission: 0.05, referee: '', notes: '' }); await load()
    } catch (e) { console.error(e) } finally { setSaving(false) }
  }

  async function settleMatch(match: any, result: string) {
    if (!confirm(`Declare result: ${MATCH_RESULT_LABELS[result as keyof typeof MATCH_RESULT_LABELS]}?`)) return
    try {
      // Get all bets for this match
      const betRes = await arenaBetRepo.findMany({ page: 1, pageSize: 1000 })
      const allBets: any[] = 'items' in betRes ? betRes.items : []
      const matchBets = allBets.filter((b: any) => b.matchId === match.id)

      // Settle
      const settlement = ArenaBettingService.settleMatch(
        { ...match, result, houseCommission: match.houseCommission ?? 0.05 }, matchBets
      )

      // Update match result
      const winnerId = result === 'fighter1_win' ? match.fighter1Id : result === 'fighter2_win' ? match.fighter2Id : undefined
      await arenaMatchRepo.update(match.id, {
        status: 'completed', result, winnerId,
        updatedBy: 'admin',
      } as any)

      // Update bet statuses
      for (const bet of [...settlement.winningBets, ...settlement.losingBets]) {
        try { await arenaBetRepo.update(bet.id, { status: bet.status, actualPayout: bet.actualPayout, settledAt: bet.settledAt, updatedBy: 'admin' } as any) }
        catch (e) { console.error(e) }
      }

      alert(`Match settled! ${settlement.winningBets.length} winning bets, ${settlement.losingBets.length} lost.\nTotal paid out: ₱${settlement.totalPaidOut.toLocaleString()}\nHouse commission: ₱${settlement.totalHouseCommission.toLocaleString()}`)
      await load()
    } catch (e: any) { alert('Error: ' + e.message) }
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div><h2 className="text-2xl font-bold text-gray-900">Matches</h2><p className="text-sm text-gray-500 mt-1">{matches.length} fights</p></div>
        <Button onClick={() => setShowModal(true)} icon={<Plus className="h-4 w-4" />}>Schedule Match</Button>
      </div>
      <Card><CardHeader title="Fight Schedule" description="Set odds & settle results" />
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50"><tr>{['Code','Fighter 1','Odds','Fighter 2','Odds','Date','Status','Result',''].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {loading ? <tr><td colSpan={9} className="py-8 text-center text-gray-400">Loading...</td></tr> :
             matches.length === 0 ? <tr><td colSpan={9} className="py-8 text-center text-gray-400">No matches scheduled.</td></tr> :
             matches.map((m: any) => (
              <tr key={m.id} className="hover:bg-gray-50">
                <td className="px-3 py-2.5 font-mono text-xs">{m.matchCode}</td>
                <td className="px-3 py-2.5 font-medium">{m.fighter1Name ?? '—'}</td>
                <td className="px-3 py-2.5 text-blue-600 font-medium">{m.fighter1Odds?.toFixed(2)}</td>
                <td className="px-3 py-2.5 font-medium">{m.fighter2Name ?? '—'}</td>
                <td className="px-3 py-2.5 text-red-600 font-medium">{m.fighter2Odds?.toFixed(2)}</td>
                <td className="px-3 py-2.5 text-xs text-gray-500">{new Date(m.scheduledDate).toLocaleDateString()}</td>
                <td className="px-3 py-2.5"><Badge color={m.status==='scheduled'?'yellow':m.status==='completed'?'green':'red'}>{MATCH_STATUS_LABELS[m.status as keyof typeof MATCH_STATUS_LABELS]??m.status}</Badge></td>
                <td className="px-3 py-2.5 text-xs">{m.result ? MATCH_RESULT_LABELS[m.result as keyof typeof MATCH_RESULT_LABELS] ?? m.result : '—'}</td>
                <td className="px-3 py-2.5">
                  {m.status === 'scheduled' && (
                    <div className="flex gap-1">
                      <button onClick={() => settleMatch(m, 'fighter1_win')} className="text-xs text-green-600 hover:underline" title="Fighter 1 Wins">F1✓</button>
                      <button onClick={() => settleMatch(m, 'fighter2_win')} className="text-xs text-red-600 hover:underline" title="Fighter 2 Wins">F2✓</button>
                      <button onClick={() => settleMatch(m, 'draw')} className="text-xs text-gray-600 hover:underline" title="Draw">Draw</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Card>
      <Modal open={showModal} onClose={()=>setShowModal(false)} title="Schedule Match" size="lg"
        footer={<div className="flex gap-2"><Button variant="secondary" onClick={()=>setShowModal(false)}>Cancel</Button><Button onClick={save} loading={saving} icon={<Trophy className="h-4 w-4"/>}>Schedule</Button></div>}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Fighter 1</label>
            <select value={form.fighter1Id} onChange={e=>setForm({...form,fighter1Id:e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              <option value="">Select...</option>{fighters.map((f:any)=><option key={f.id} value={f.id}>{f.name} ({f.breed})</option>)}</select></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Fighter 2</label>
            <select value={form.fighter2Id} onChange={e=>setForm({...form,fighter2Id:e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              <option value="">Select...</option>{fighters.map((f:any)=><option key={f.id} value={f.id}>{f.name} ({f.breed})</option>)}</select></div>
          <Input label="Match Code" value={form.matchCode} onChange={e=>setForm({...form,matchCode:e.target.value})} />
          <Input label="Date" type="datetime-local" value={new Date(form.scheduledDate).toISOString().slice(0,16)} onChange={e=>setForm({...form,scheduledDate:new Date(e.target.value).getTime()})} />
          <Input label="F1 Odds" type="number" value={String(form.fighter1Odds)} onChange={e=>setForm({...form,fighter1Odds:Number(e.target.value)})} />
          <Input label="F2 Odds" type="number" value={String(form.fighter2Odds)} onChange={e=>setForm({...form,fighter2Odds:Number(e.target.value)})} />
          <Input label="Draw Odds" type="number" value={String(form.drawOdds)} onChange={e=>setForm({...form,drawOdds:Number(e.target.value)})} />
          <Input label="House %" type="number" value={String(form.houseCommission)} onChange={e=>setForm({...form,houseCommission:Number(e.target.value)})} />
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Weight Class</label>
            <select value={form.weightClass} onChange={e=>setForm({...form,weightClass:e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              {Object.entries(WEIGHT_CLASS_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select></div>
          <Input label="Referee" value={form.referee} onChange={e=>setForm({...form,referee:e.target.value})} />
        </div>
      </Modal>
    </div>
  )
}
